import { getQuestionContent } from 'src/helper/questionHelper';
import type { AuditMetrics, QuestionWithResponse } from 'src/types/helios/Audit';
import type {
  InsightQuestion,
  ResponseBreakdown,
} from 'src/v2/features/audit-insights/components/IncompleteAuditInsights';

/**
 * Shapes the audit's questions for the insights tab, resolved to the reader's language.
 *
 * Kept out of the tab itself so the tab takes a plain view model and never reads a question's
 * raw translation shape. This is the seam between the legacy audit summary page and the tab:
 * it is the one place in the feature that reads `src/helper/questionHelper` and the legacy
 * `Audit` types, so when that page goes the whole adapter goes with it.
 *
 * @param questions - Questions as the audit summary returns them.
 * @param language - The reader's selected language. Falls back to the question's default.
 */
export function toInsightQuestions(
  questions: QuestionWithResponse[],
  language?: string,
): InsightQuestion[] {
  return questions.map((question) => {
    const content = getQuestionContent(
      question.questionDetails,
      language,
    ).questionDetails;

    return {
      questionId: question.questionDetails.questionId,
      title: content.title,
      questionText: content.questionText,
      points: question.questionDetails.auditScore,
      result: question.responseDetails?.mainQuestionResponse?.responseResult,
    };
  });
}

/**
 * Shapes the audit's own pass/fail/NA counts for the insights tab.
 *
 * Returns undefined when the audit reports none, so the tab can tell "not scored" from a
 * scored zero. Counts are never recomputed from the responses.
 */
export function toResponseBreakdown(
  metrics: AuditMetrics | undefined,
): ResponseBreakdown | undefined {
  if (!metrics) {
    return undefined;
  }

  return {
    passed: metrics.passedQuestions,
    failed: metrics.failedQuestions,
    na: metrics.naQuestions,
    total: metrics.totalQuestions,
  };
}
